import { useContext, useEffect, useState } from 'react';

import { Disclosure } from '@headlessui/react';
import clsx from 'clsx';
import { useLocation, useNavigate } from 'react-router-dom';

import { ContactMenu } from '@Components/Progress/ContactMenu';
import { ApplicationContext } from '@src/app/contexts/Application';
import { DebtConContext } from '@src/app/contexts/DebtCon';
import { useTrackProgressBarTapped } from '@src/app/hooks/trackMixpanelEventsHooks/useTrackProgressBarTapped';
import { ApplicationStage } from '@src/app/interfaces/application-stage.enum';
import { CreditCommitmentsData } from '@src/app/interfaces/debtcon';
import { containsAnySubStr } from '@src/shared/helpers/string';

import PercentageCompleteIcon from '../Icons/PercentageCompleteIcon';
import PercentageIncompleteIcon from '../Icons/PercentageIncompleteIcon';
import ProgressChevronIcon from '../Icons/ProgressChevron';

const className = {
    base: 'flex flex-col item-center space-y-2 sm:w-80 lg:w-96 hidden sm:block',
    wrapper: 'bg-white p-7 rounded-lg',
    progress_wrapper: 'mb-4',
    progress_paragraph: 'text-xs font-bold text-periwinkle-900',
    progress_inner_wrapper: 'h-2 bg-almond-500 mt-2 text-center rounded-full',
    progress_inner_bar: 'h-full bg-periwinkle-700 rounded-full',
    list_wrapper:
        'flex justify-between items-center space-x-2 mb-1 rounded p-2 cursor-pointer',
    unordered_list_wrapper: 'mt-6',
    inner_list_wrapper: 'flex-1 ml-2 opacity-50',
    paragraph: 'text-body font-sora-bold',
    paragraph_small:
        'text-base -mt-1 text-steel-40 text-xs hover:text-steel-60',
    paragraph_quote: 'flex-1 ml-2 text-xl font-bold opacity-50',
    phone_wrapper:
        'bg-white text-sm_body px-4 py-3 rounded-t-xl rounded-bl rounded-bl-xl',
    canEdit: 'border-neutral-100',

    // small screen base
    sm_base:
        'w-full block sm:hidden absolute left-0 p-0 md:relative md:left-auto md:w-auto md:p-4',
    sm_wrapper: 'w-full rounded-b-2xl bg-white shadow-md py-2',
    sm_progress_wrapper: 'w-1/2',
    sm_disclosure_btn:
        'flex w-full justify-between rounded-b-lg bg-white px-4 py-2 text-left hover:bg-white focus:outline-none focus-visible:ring focus-visible:ring-white/75',
    sm_disclosure_panel: 'px-4 pb-2 pt-4 text-sm h-screen',
    sm_phone_wrapper:
        'bg-white p-4 rounded-lg mt-16 mb-8 border-1 border-steel-100 shadow-contactMenu',

    // actions
    hover: 'hover:bg-neutral-40',
    activeBackground: 'bg-almond-100',
    inner_active_list_wrapper: 'flex-1 ml-2',
    text_hover: 'hover:opacity-100',
};

export interface ProgressMenuProps {
    className?: string;
}

const ProgressMenu = (props: ProgressMenuProps) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [isRejected, setIsRejected] = useState<boolean>(false);

    const {
        debtConUser,
        dashboardItemStatuses,
        applicationStage,
        loanDetails,
    } = useContext(ApplicationContext);

    const { creditCommitmentsData } = useContext(DebtConContext);

    useEffect(
        () =>
            setIsRejected(
                containsAnySubStr(applicationStage || '', [
                    ApplicationStage.DECISIONING_DECLINE,
                    ApplicationStage.CLOSED,
                ]),
            ),
        [applicationStage],
    );

    const url = new URL(window.location.href);

    const baseClassName = clsx([props.className, className.base]);

    const listClassName = clsx([className.list_wrapper, className.hover]);

    const listClassNameWithBackground = clsx([
        className.list_wrapper,
        className.activeBackground,
    ]);

    const innerListClassName = clsx([
        className.inner_list_wrapper,
        className.text_hover,
    ]);

    const progressMobileParagraph = clsx([className.progress_paragraph]);

    const canEdit = clsx([className.canEdit, listClassName]);
    const canHover = clsx(className.list_wrapper);

    const getCompletedStatus = (stage: string) =>
        dashboardItemStatuses.find((item: any) => item.name === stage)
            ?.completed;

    // user will have extra step if they are a debt con user
    const expectedTotalSteps = debtConUser ? 8 : 7;

    // reduce steps by 1 if user is debt con, offer
    // reduce steps by 2 is user isnt part of debt con, offer and debt con
    const adjustedTotalSteps = debtConUser
        ? dashboardItemStatuses.length - 1
        : dashboardItemStatuses.length - 2;

    const completedSteps = !debtConUser
        ? dashboardItemStatuses.filter(
              (item: { completed: any; name: string }) =>
                  item.completed && item.name !== 'debt-con',
          )
        : dashboardItemStatuses.filter(
              (item: { completed: any }) => item.completed,
          );

    const completeStepsLength =
        completedSteps.length === expectedTotalSteps
            ? adjustedTotalSteps
            : completedSteps.length;

    const progress = Math.floor(
        (completeStepsLength / adjustedTotalSteps) * 100,
    );

    const generateMenuItem = (
        name: string,
        title: string,
        index: number,
        dashboardItemStatuses: any,
        time?: string,
    ) => {
        return {
            name: name,
            title: title,
            time: time,
            disabled: dashboardItemStatuses[index].disabled,
            notCompleteNotdisabled:
                !dashboardItemStatuses[index].disabled &&
                !dashboardItemStatuses[index].completed,
            hoverState: !dashboardItemStatuses[index].completed
                ? className.list_wrapper
                : listClassName,
            innerListHoverState: !dashboardItemStatuses[index].completed
                ? className.inner_list_wrapper
                : innerListClassName,
        };
    };

    const menuItems = [
        generateMenuItem(
            'loan-details',
            'Your loan',
            0,
            dashboardItemStatuses,
            '1 minute',
        ),
        generateMenuItem(
            'applicants',
            'About You',
            1,
            dashboardItemStatuses,
            '1 minute',
        ),
        generateMenuItem(
            'property-details',
            'Your property',
            2,
            dashboardItemStatuses,
            '2 minutes',
        ),
        generateMenuItem(
            'income',
            'Your income',
            3,
            dashboardItemStatuses,
            '1 minute',
        ),
        generateMenuItem(
            'outgoings',
            'Your spending',
            4,
            dashboardItemStatuses,
            '1 minute',
        ),
        generateMenuItem(
            'debt-con',
            'Your borrowings',
            5,
            dashboardItemStatuses,
        ),
        generateMenuItem(
            'loan-preferences',
            'Customise your quote',
            6,
            dashboardItemStatuses,
            '2 minutes',
        ),
    ];

    if (!debtConUser) {
        delete menuItems[5];
    }

    const handleNavigate = (
        itemName: string,
        isDisabled: boolean,
        isCompleted: boolean,
        isRejected: boolean,
    ) => {
        if (isDisabled) return;

        let route;

        switch (itemName) {
            case 'reject':
                if (isCompleted && isRejected) {
                    route = 'reject';
                }
                break;

            case 'create-account':
                route = 'create-account';
                break;

            case 'debt-con':
                if (!loanDetails.debtConMainGoal) {
                    route = 'debt-con/main-goal';
                } else if (creditCommitmentsData?.length > 0) {
                    const debtsNotComplete = creditCommitmentsData.some(
                        (debt: CreditCommitmentsData) =>
                            !debt.lenderName &&
                            !debt.dffDebtConSkipped &&
                            !debt.ignore,
                    );
                    route = `debt-con/${
                        debtsNotComplete ? 'borrow' : 'review'
                    }`;
                } else {
                    route = 'debt-con/intro';
                }
                break;

            case 'loan-preferences':
                route = 'loan-preferences/homepage';
                break;

            default:
                route = itemName;
                break;
        }

        if (route) {
            navigate(`/eligibility/${route}`);
        }
    };

    const progressMenuItems = () => {
        return (
            <>
                {menuItems.map((item) => (
                    <li
                        key={item.name}
                        className={
                            url.href.includes(item.name)
                                ? listClassNameWithBackground
                                : item.notCompleteNotdisabled
                                ? canEdit
                                : item.hoverState
                        }
                        onClick={() =>
                            handleNavigate(
                                item.name,
                                item.disabled,
                                false,
                                false,
                            )
                        }
                    >
                        <div
                            className={
                                url.href.includes(item.name)
                                    ? className.inner_active_list_wrapper
                                    : item.notCompleteNotdisabled
                                    ? innerListClassName
                                    : item.innerListHoverState
                            }
                        >
                            <p className={className.paragraph}>{item.title}</p>
                            <p className={className.paragraph_small}>
                                {item.time}
                            </p>
                        </div>
                        {getCompletedStatus(item.name) ? (
                            <PercentageCompleteIcon />
                        ) : (
                            <PercentageIncompleteIcon />
                        )}
                    </li>
                ))}

                <li
                    className={
                        !dashboardItemStatuses[7].disabled &&
                        !dashboardItemStatuses[7].completed
                            ? canEdit
                            : listClassName
                            ? canHover
                            : listClassName
                    }
                    onClick={() =>
                        handleNavigate(
                            dashboardItemStatuses[7].completed
                                ? 'reject'
                                : 'create-account',
                            dashboardItemStatuses[7].disabled,
                            dashboardItemStatuses[7].completed,
                            isRejected,
                        )
                    }
                >
                    <div
                        className={
                            !dashboardItemStatuses[6].completed
                                ? className.inner_list_wrapper
                                : innerListClassName
                        }
                    >
                        <p className={className.paragraph}>Your quote</p>
                    </div>
                </li>
            </>
        );
    };

    const trackProgressBarTapped = useTrackProgressBarTapped(location.pathname);

    return (
        <>
            <div className={baseClassName}>
                <div className={className.wrapper}>
                    <div className={className.progress_wrapper}>
                        <p className={className.progress_paragraph}>
                            {progress}% complete
                        </p>
                        <div className={className.progress_inner_wrapper}>
                            <div
                                className={className.progress_inner_bar}
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>
                    </div>

                    <ul className={className.unordered_list_wrapper}>
                        {progressMenuItems()}
                    </ul>
                </div>

                <ContactMenu wrapperClassName={className.phone_wrapper} />
            </div>

            <div className={className.sm_base}>
                <div className={className.sm_wrapper}>
                    <Disclosure as='div'>
                        {({ open }) => (
                            <>
                                <Disclosure.Button
                                    className={className.sm_disclosure_btn}
                                    onClick={() => {
                                        if (!open) {
                                            trackProgressBarTapped();
                                        }
                                    }}
                                >
                                    <p className={progressMobileParagraph}>
                                        {progress}% complete
                                    </p>
                                    <div
                                        className={
                                            className.sm_progress_wrapper
                                        }
                                    >
                                        <div
                                            className={
                                                className.progress_inner_wrapper
                                            }
                                        >
                                            <div
                                                className={
                                                    className.progress_inner_bar
                                                }
                                                style={{
                                                    width: `${progress}%`,
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                    <ProgressChevronIcon
                                        className={`${
                                            open ? 'rotate-180 transform' : ''
                                        } h-5 w-5 text-steel-80`}
                                    />
                                </Disclosure.Button>
                                <Disclosure.Panel
                                    className={className.sm_disclosure_panel}
                                >
                                    <ul>{progressMenuItems()}</ul>

                                    <ContactMenu
                                        wrapperClassName={
                                            className.sm_phone_wrapper
                                        }
                                    />
                                </Disclosure.Panel>
                            </>
                        )}
                    </Disclosure>
                </div>
            </div>
        </>
    );
};

export default ProgressMenu;
